import React from 'react';
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
} from 'recharts';
import { AnalyticsAggregation } from '../../services/analyticsApi';

interface ComparisonBarChartProps {
  data: AnalyticsAggregation[];
  title: string;
  xAxisLabel?: string;
  showPercentage?: boolean;
  height?: number;
}

const ComparisonBarChart: React.FC<ComparisonBarChartProps> = ({
  data,
  title,
  xAxisLabel = 'Category',
  showPercentage = false,
  height = 400,
}) => {
  // Custom tick that wraps long labels into two lines
  const CustomXAxisTick = ({ x, y, payload }: any) => {
    const words = payload.value.split(' ');
    const mid = Math.ceil(words.length / 2);
    const line1 = words.slice(0, mid).join(' ');
    const line2 = words.slice(mid).join(' ');
    return (
      <g transform={`translate(${x},${y})`}>
        <text
          x={0}
          y={0}
          textAnchor="end"
          fill="#666"
          fontSize={12}
          transform="rotate(-35)"
        >
          <tspan x={0} dy="0">{line1}</tspan>
          {line2 && <tspan x={0} dy="14">{line2}</tspan>}
        </text>
      </g>
    );
  };

  // Transform data for the chart
  const chartData = data.map((item) => ({
    name: item.category,
    'PM-SHRI': item.pmshriCount,
    'Non PM-SHRI': item.nonPmshriCount,
    'PM-SHRI %': item.pmshriPercentage.toFixed(1),
    total: item.total,
  }));

  const CustomTooltip = ({ active, payload, label }: any) => {
    if (active && payload && payload.length) {
      const pmshri = payload.find((p: any) => p.dataKey === 'PM-SHRI');
      const nonPmshri = payload.find((p: any) => p.dataKey === 'Non PM-SHRI');
      const total = (pmshri?.value || 0) + (nonPmshri?.value || 0);
      const pmshriPct = total > 0 ? ((pmshri?.value || 0) / total) * 100 : 0;

      return (
        <div className="bg-white p-4 border border-gray-200 rounded-lg shadow-lg">
          <p className="font-semibold text-gray-800 mb-2">{label}</p>
          <div className="space-y-1">
            <p className="text-green-600">
              <span className="inline-block w-3 h-3 rounded-full bg-green-500 mr-2"></span>
              PM-SHRI: {pmshri?.value?.toLocaleString() || 0}
            </p>
            <p className="text-blue-600">
              <span className="inline-block w-3 h-3 rounded-full bg-blue-500 mr-2"></span>
              Non PM-SHRI: {nonPmshri?.value?.toLocaleString() || 0}
            </p>
            <p className="text-gray-600 text-sm border-t pt-1 mt-1">
              Total: {total.toLocaleString()} ({pmshriPct.toFixed(1)}% PM-SHRI)
            </p>
          </div>
        </div>
      );
    }
    return null;
  };

  if (!data || data.length === 0) {
    return (
      <div className="bg-white rounded-xl border border-gray-200 p-6">
        <h3 className="text-lg font-semibold text-gray-800 mb-4">{title}</h3>
        <div className="flex items-center justify-center h-64 text-gray-400">
          No data available
        </div>
      </div>
    );
  }

  return (
    <div className="bg-white rounded-xl border border-gray-200 p-6">
      <h3 className="text-lg font-semibold text-gray-800 mb-4">{title}</h3>
      <ResponsiveContainer width="100%" height={height}>
        <BarChart
          data={chartData}
          margin={{ top: 20, right: 30, left: 20, bottom: 60 }}
        >
          <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
          <XAxis
            dataKey="name"
            interval={0}
            height={90}
            padding={{ left: 20, right: 20 }}
            tick={<CustomXAxisTick />}
            tickMargin={10}
            axisLine={{ stroke: '#e5e7eb', strokeWidth: 1 }}
            tickLine={false}
          />
          <YAxis
            tick={{ fontSize: 12, fill: '#666' }}
            axisLine={{ stroke: '#e5e7eb', strokeWidth: 1 }}
            tickLine={false}
            label={{
              value: 'Numbers',
              angle: -90,
              position: 'insideLeft',
              dx: -20,
              dy: 60,
              fill: '#666',
            }}
          />
          <Tooltip content={<CustomTooltip />} />
          <Legend
            verticalAlign="bottom"
            align="center"
            wrapperStyle={{ marginBottom: -80 }}
            iconType="circle"
          />
          <Bar
            dataKey="PM-SHRI"
            fill="#22c55e"
            radius={[4, 4, 0, 0]}
            name="PM-SHRI Schools"
          />
          <Bar
            dataKey="Non PM-SHRI"
            fill="#3b82f6"
            radius={[4, 4, 0, 0]}
            name="Non PM-SHRI Schools"
          />
        </BarChart>
      </ResponsiveContainer>
    </div>
  );
};

export default ComparisonBarChart;
